import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import { useIsAuthenticated } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import axios from "axios";
import { msalInstance, loginRequest, trialLoginRequest, getExternalIdInstance } from "../authConfig";
import { getAppConfig } from "../appConfig";

// Data fields stored in state (no function references)
interface UserData {
  isTrial:         boolean;
  isAdmin:         boolean;
  userEmail:       string;
  userName:        string;
  firstName:       string;
  lastName:        string;
  companyName:     string;
  jobTitle:        string;
  country:         string;
  tenantName:      string;
  subscriptionTier: string;
  profileComplete: boolean;
  runsUsed:        number;
  runLimit:        number;
  trialExpiry:     string;
  trialExpired:    boolean;
  loading:         boolean;
  authError:       AuthError | null;
}

export interface AuthError {
  code:    string;
  message: string;
  detail?: string;
}

// Context type adds refreshUser so consumers can re-fetch after a scan
export interface UserContextType extends UserData {
  refreshUser: () => void;
}

const defaultData: UserData = {
  isTrial:         false,
  isAdmin:         true,
  userEmail:       "",
  userName:        "",
  firstName:       "",
  lastName:        "",
  companyName:     "",
  jobTitle:        "",
  country:         "",
  tenantName:      "",
  subscriptionTier: "",
  profileComplete: true,
  runsUsed:        0,
  runLimit:        5,
  trialExpiry:     "",
  trialExpired:    false,
  loading:         true,
  authError:       null,
};

const UserContext = createContext<UserContextType>({ ...defaultData, refreshUser: () => {} });

export function useUser(): UserContextType {
  return useContext(UserContext);
}

/**
 * Resolves the active MSAL account and instance.
 * Prefers the main Azure AD instance; falls back to External ID for trial users.
 * Mirrors the same logic in configApi.ts so both always use the same token.
 */
function resolveActiveAuth() {
  const mainAccount = msalInstance.getActiveAccount()
    ?? msalInstance.getAllAccounts().find(a => !a.environment.includes("ciamlogin.com"));
  if (mainAccount) {
    return { account: mainAccount, instance: msalInstance, request: loginRequest };
  }
  const extId = getExternalIdInstance();
  if (extId) {
    const extAccount = extId.getActiveAccount() ?? extId.getAllAccounts()[0];
    if (extAccount) {
      return { account: extAccount, instance: extId, request: trialLoginRequest };
    }
  }
  return null;
}

export function UserProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const [userData, setUserData] = useState<UserData>(defaultData);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const auth = resolveActiveAuth();
      if (!auth) {
        setUserData({ ...defaultData, loading: false });
        return;
      }

      // For CIAM (External ID) accounts use the token stored in sessionStorage
      // by index.tsx — acquireTokenSilent throws authority_mismatch for CIAM in MSAL v5.
      const isCiam = auth.account.environment.includes("ciamlogin.com");
      let accessToken: string;

      if (isCiam) {
        const stored = sessionStorage.getItem("extid_token");
        const parsed = stored ? JSON.parse(stored) : null;
        if (!parsed || (parsed.expiresOn && new Date(parsed.expiresOn) <= new Date())) {
          auth.instance.loginRedirect(auth.request);
          return;
        }
        accessToken = parsed.accessToken;
      } else {
        const result = await auth.instance.acquireTokenSilent({
          ...auth.request,
          account: auth.account,
        });
        accessToken = result.accessToken;
      }

      // ID token claims — shown immediately so the name appears without waiting for the API.
      const idClaims = (auth.account.idTokenClaims ?? {}) as Record<string, any>;
      // External ID sets displayName to "Unknown" for new users before profile update — treat as absent.
      const validName = (s?: string | null) => (s && s.toLowerCase() !== "unknown") ? s : "";
      const nameFromIdToken =
        [idClaims["given_name"], idClaims["family_name"]].map(validName).filter(Boolean).join(" ").trim() ||
        validName(idClaims["name"]) ||
        validName(auth.account.name) ||
        auth.account.username?.split("@")[0] || "";
      const emailFromIdToken = idClaims["email"] || idClaims["preferred_username"] || auth.account.username || "";

      // Show name & email immediately from the token — no spinner wait for user identity
      setUserData(prev => ({
        ...prev,
        userName:  nameFromIdToken,
        userEmail: emailFromIdToken,
      }));

      const response = await axios.get(
        `${getAppConfig().apiBase}/GetCurrentUser`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      setUserData({
        isTrial:          response.data.isTrial          ?? false,
        isAdmin:          response.data.isAdmin          ?? true,
        tenantName:       response.data.tenantName        ?? "",
        subscriptionTier: response.data.subscriptionTier  ?? "",
        userEmail:        response.data.userEmail          ?? "",
        userName:         response.data.userName           || nameFromIdToken,
        firstName:        response.data.firstName          ?? "",
        lastName:         response.data.lastName           ?? "",
        companyName:      response.data.companyName        ?? "",
        jobTitle:         response.data.jobTitle           ?? "",
        country:          response.data.country            ?? "",
        profileComplete:  response.data.profileComplete    ?? true,
        runsUsed:         response.data.runsUsed           ?? 0,
        runLimit:         response.data.runLimit           ?? 5,
        trialExpiry:      response.data.trialExpiry        ?? "",
        trialExpired:     response.data.trialExpired       ?? false,
        loading: false,
        authError: null,
      });
    } catch (err: any) {
      if (err instanceof InteractionRequiredAuthError) {
        // Token refresh required — trigger interactive login
        const auth2 = resolveActiveAuth();
        if (auth2) auth2.instance.loginRedirect(auth2.request);
        return;
      }

      // Backend GetCurrentUser failures are surfaced as a proper error screen
      // (see AuthErrorScreen) instead of silently signing the user back out —
      // a blind logoutRedirect here just loops the user with no explanation.
      const status = err?.response?.status;
      const data = err?.response?.data;

      let code = "SERVER_ERROR";
      let message = "Something went wrong while loading your account. Please try again, or contact support if the problem continues.";
      let detail: string | undefined;

      if (data && typeof data === "object" && data.errorCode) {
        code = data.errorCode;
        message = data.message || message;
        detail = data.detail;
      } else if (status === 401) {
        code = "TOKEN_INVALID";
        message = "Your session could not be verified. Please sign in again.";
      }

      setUserData({ ...defaultData, loading: false, authError: { code, message, detail } });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Check External ID accounts synchronously — index.tsx processed the redirect
    // before first render, so getAllAccounts() is reliable here.
    const extId = getExternalIdInstance();
    const isExtIdAuthenticated = (extId?.getAllAccounts().length ?? 0) > 0;
    const effectivelyAuthenticated = isAuthenticated || isExtIdAuthenticated;

    if (!effectivelyAuthenticated) {
      setUserData({ ...defaultData, loading: false });
      return;
    }

    fetchCurrentUser();
  }, [isAuthenticated, fetchCurrentUser]);

  return (
    <UserContext.Provider value={{ ...userData, refreshUser: fetchCurrentUser }}>
      {children}
    </UserContext.Provider>
  );
}
